/**
 * LiveSessionsSection.tsx
 *
 * Renders live and previous-live horizontal media rails.
 * Handles session hydration, previous-list pagination, and CT tracking payloads.
 */

import { dataLayer } from "@repo/analytics/data-layer-utils";
import { dlElementConstants } from "@repo/analytics/data-layer.constants";
import { getSessionData } from "@repo/shared-utils/common-utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FWN_SCRIPT_URL } from "../../../constants/firework";
import type { LiveStream, MediaData } from "../../../types/livelanding.types";
import { getSubsectionFromHashtags } from "../../../utils/mediaUtils";
import { getCtSectionTitle } from "../../HeadlessPlaylistSection/headlessPlaylistUtils";
import { UnifiedMediaCard } from "../../MediaCard/UnifiedMediaCard";
import { Skeleton } from "../../SkeletonLoader/SkeletonLoader";
import { PLAYLIST_VIDEOS_PATH_RE, FW_PLAYER_SKELETON_MIN_MS } from "../constants";
import { HorizontalCardSkeleton } from "../components/SectionSkeletons";
import { useFireworkSkeletonGate } from "../hooks/useFireworkSkeletonGate";
import { UnifiedMediaSectionProps } from "../types";

const LIVE_SECTION_CLASS =
  "border-t-2 border-b-2 border-gray-200 bg-white px-[16px] pb-[10px] md:px-[24px] md:pb-[12px]";
const PREVIOUS_SECTION_CLASS =
  "border-b-2 border-t-2 border-gray-200 bg-white px-5 py-2 sm:!py-5 sm:px-2.5 md:!py-2 md:px-5";
const LIVE_STREAM_ITEM_CLASS =
  "min-w-[110px] shrink-0 sm:min-w-[140px] md:min-w-[190px] lg:min-w-[200px] xl:min-w-[210px]";

function getPaginationPath(nextUrl: string): string | null {
  try {
    const url = new URL(nextUrl, window.location.origin);
    const page = url.searchParams.get("page");
    const queryPlaylistId = url.searchParams.get("playlistId");
    const pathMatch = PLAYLIST_VIDEOS_PATH_RE.exec(url.pathname);
    const playlistId = queryPlaylistId ?? pathMatch?.[1];

    if (!playlistId || !page) return null;

    return `/api/v1/live-streams?playlistId=${encodeURIComponent(playlistId)}&page=${encodeURIComponent(page)}`;
  } catch {
    return null;
  }
}

function getLiveStreamKey(video: LiveStream): string | undefined {
  return video.id ?? video.encoded_id ?? video.trailer?.encoded_id;
}

function mergeLiveStreams(
  previousData: MediaData | null,
  newStreams: LiveStream[],
  next: string | null | undefined
): MediaData {
  const base: MediaData = previousData ?? { live_streams: [] };
  const previousStreams = Array.isArray(base.live_streams) ? base.live_streams : [];
  const seen = new Set<string>();
  const liveStreams = [...previousStreams, ...newStreams].filter((video) => {
    const key = getLiveStreamKey(video);
    if (!key) return true;

    const normalizedKey = String(key);
    if (seen.has(normalizedKey)) return false;

    seen.add(normalizedKey);
    return true;
  });

  return {
    ...base,
    live_streams: liveStreams,
    paging: { ...base.paging, next },
  };
}

function matchesVideoId(video: LiveStream, videoEncodedId: string): boolean {
  return (
    video.encoded_id === videoEncodedId ||
    video.id === videoEncodedId ||
    video.trailer?.encoded_id === videoEncodedId
  );
}

function getTrimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  return value.trim() || undefined;
}

function getVideoCategory(video: LiveStream): string {
  return (
    getTrimmedString(video.event_description) ??
    getTrimmedString(video.caption) ??
    getTrimmedString(video.event_name) ??
    "NA"
  );
}

type PreviousLiveClickParams = {
  videoEncodedId: string;
  mediaData: MediaData | null;
  isLiveMode: boolean;
  ctSectionTitle: string;
  customerId: string;
};

function trackPreviousLiveClick({
  videoEncodedId,
  mediaData,
  isLiveMode,
  ctSectionTitle,
  customerId,
}: PreviousLiveClickParams): void {
  if (isLiveMode || !Array.isArray(mediaData?.live_streams)) return;

  const positionIndex = mediaData.live_streams.findIndex((video) =>
    matchesVideoId(video, videoEncodedId)
  );
  if (positionIndex < 0) return;

  const video = mediaData.live_streams[positionIndex];
  if (!video) return;

  const position = String(positionIndex + 1);
  const videoId = video.encoded_id || video.id || "NA";
  const videoImpressionId = video.trailer?.encoded_id || videoId;
  const categoryFromVideo = getVideoCategory(video);
  const cleverTapObject = {
    EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
    EP_COMPONENT_NAME: "PREVIOUS_LIVE_VIDEO",
    EP_PAGE_NAME: dlElementConstants.LIVE_COMMERCE_CLP,
    EP_SECTION: ctSectionTitle,
    EP_CTA: "WATCHNOW",
    EP_CTA_TEXT: "WATCHNOW",
    EP_VIDEO_TITLE: video.caption || video.event_name || "NA",
    EP_LIVEVIDEO_IMPRESSION: videoImpressionId,
    EP_ID: videoImpressionId,
    EP_LIVE_SESSION: video.live_stream_status === "active" ? "ACTIVE" : "INACTIVE",
    EP_VIDEO_TYPE: "VIDEO_POP_UP",
    EP_JOURNEY: "LIVE_COMMERCE",
    EP_SEGMENT: "LIVE_COMMERCE",
    EP_POSITION: position,
    EP_SR_NUMBER: position,
    EP_CUSTOMER_TYPE: "NTB",
    EP_SPRINT: "DPAOP 3",
    EP_DESTINATION: "NA",
    EP_SUB_SECTION: getSubsectionFromHashtags(video.trailer?.hashtags),
    EP_NUDGE: "NA",
    EP_SEARCH: "NA",
    EP_CUSTOMER_ID: customerId,
    EP_PRODUCT_NAME: categoryFromVideo,
    EP_PRODUCT_CATEGORY: `${categoryFromVideo} || ${videoImpressionId}`,
    EP_PAGE_URL: typeof window !== "undefined" ? window.location.href : "NA",
    EP_EVENT_TYPE: "CLICK",
    EP_LANG: "eng",
  };

  dataLayer("", {}, cleverTapObject);
}

type LiveStreamsListProps = {
  streams: LiveStream[];
  isFetchingMore: boolean;
  isLiveMode: boolean;
  onScrollEnd: (element: HTMLUListElement | null) => void;
  onThumbnailClick: (videoEncodedId: string) => void;
};

function LiveStreamsList({
  streams,
  isFetchingMore,
  isLiveMode,
  onScrollEnd,
  onThumbnailClick,
}: Readonly<LiveStreamsListProps>) {
  return (
    <ul
      className="relative flex [scrollbar-width:none] list-none items-start gap-[7px] overflow-x-auto scroll-smooth [-ms-overflow-style:none] sm:gap-[6px] md:gap-[10px] [&::-webkit-scrollbar]:hidden"
      onScroll={(event) => onScrollEnd(event.currentTarget)}
    >
      {streams.map((video) => (
        <li key={getLiveStreamKey(video)} className={LIVE_STREAM_ITEM_CLASS}>
          <UnifiedMediaCard
            variant="media"
            video={video}
            ended_at={video.ended_at}
            onThumbnailClick={onThumbnailClick}
            liveOverride={isLiveMode}
            textColor="dark"
          />
        </li>
      ))}
      {isFetchingMore && !isLiveMode && (
        <li className={LIVE_STREAM_ITEM_CLASS}>
          <Skeleton
            variant="rectangular"
            className="mb-3 aspect-[9/15.8] w-full rounded-lg bg-gray-200 sm:aspect-[9/16.3]"
            aria-hidden="true"
          />
        </li>
      )}
    </ul>
  );
}

/**
 * LiveSessionsSection
 *
 * Handles both `mode="live"` and `mode="previous"` rendering.
 * Responsible for live/previous stream listing, CT click payload emission,
 * and pagination-on-scroll for previous sessions.
 */
export function LiveSessionsSection({
  title,
  titleCt,
  data,
  mode,
  apiBaseUrl = "",
  reduceMobileSectionHeadingSize = false,
}: Readonly<
  Pick<
    UnifiedMediaSectionProps,
    "title" | "titleCt" | "data" | "mode" | "apiBaseUrl" | "reduceMobileSectionHeadingSize"
  >
>) {
  const isLiveMode = mode === "live";
  const ctSectionTitle = getCtSectionTitle(titleCt, title);

  const [customerId, setCustomerId] = useState("NA");
  const [mediaData, setMediaData] = useState<MediaData | null>(null);
  const [nextUrl, setNextUrl] = useState<string | null | undefined>(data?.paging?.next);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const fetchedPagesRef = useRef<Set<string>>(new Set());
  const containerRef = useRef<HTMLElement | null>(null);
  const fwScriptRef = useRef<HTMLScriptElement | null>(null);
  const fwLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    getSessionData()
      .then((session) => {
        if (!isMounted) return;
        setCustomerId(session?.custId || "NA");
      })
      .catch(() => {
        if (!isMounted) return;
        setCustomerId("NA");
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (data) {
      setMediaData(data);
      setNextUrl(data?.paging?.next);
      return;
    }

    setMediaData(null);
    setNextUrl(null);

    const existing = document.querySelector<HTMLScriptElement>('script[data-fw="fwn"]');
    if (existing) {
      fwScriptRef.current = existing;
      if (existing.dataset.ready === "true") {
        fwLoadedRef.current = true;
      } else {
        const onLoad = () => {
          fwLoadedRef.current = true;
          existing.dataset.ready = "true";
        };
        existing.addEventListener("load", onLoad);
        return () => existing.removeEventListener("load", onLoad);
      }
    } else {
      const script = document.createElement("script");
      script.src = FWN_SCRIPT_URL;
      script.async = true;
      script.dataset.fw = "fwn";
      script.addEventListener("load", () => {
        fwLoadedRef.current = true;
        script.dataset.ready = "true";
      });
      script.addEventListener("error", () => {
        fwLoadedRef.current = false;
      });
      document.body.appendChild(script);
      fwScriptRef.current = script;
    }
  }, [data]);

  const handlePreviousScrollEnd = useCallback(
    async (el: HTMLUListElement | null) => {
      if (isLiveMode || !nextUrl || isFetchingMoreRef.current || !el) return;

      const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
      if (remaining > 20) return;
      if (fetchedPagesRef.current.has(nextUrl)) return;

      const paginationPath = getPaginationPath(nextUrl);
      if (!paginationPath) return;

      fetchedPagesRef.current.add(nextUrl);
      isFetchingMoreRef.current = true;
      setIsFetchingMore(true);

      try {
        const res = await fetch(`${apiBaseUrl}${paginationPath}`, {
          cache: "no-store",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`Pagination fetch failed: ${res.status}`);

        const json = await res.json();
        const newStreams: LiveStream[] = json?.data?.upcomingliveFromFireworks?.live_streams ?? [];
        const newNext: string | null | undefined =
          json?.data?.upcomingliveFromFireworks?.paging?.next ?? null;

        setMediaData((previousData) => mergeLiveStreams(previousData, newStreams, newNext));
        setNextUrl(newNext);
      } catch {
        // no-op
      } finally {
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
      }
    },
    [apiBaseUrl, isLiveMode, nextUrl]
  );

  /**
   * Handles thumbnail click and emits previous-live analytics payload when applicable.
   */
  const handleThumbnailClick = useCallback(
    (videoEncodedId: string) => {
      trackPreviousLiveClick({
        videoEncodedId,
        mediaData,
        isLiveMode,
        ctSectionTitle,
        customerId,
      });
    },
    [ctSectionTitle, customerId, isLiveMode, mediaData]
  );

  const streams: LiveStream[] = useMemo(() => {
    if (!Array.isArray(mediaData?.live_streams)) return [];
    return isLiveMode
      ? mediaData.live_streams
      : mediaData.live_streams.filter((v) => v?.status === "replay");
  }, [isLiveMode, mediaData]);

  const isContentReady = useFireworkSkeletonGate(streams.length > 0, FW_PLAYER_SKELETON_MIN_MS);
  const skeletonCount = Math.max(data?.live_streams?.length ?? 0, 5);

  const sectionClass = isLiveMode ? LIVE_SECTION_CLASS : PREVIOUS_SECTION_CLASS;

  const headingSizeClassName = reduceMobileSectionHeadingSize
    ? "text-[16px] md:text-[20px]"
    : "text-[20px]";

  if (isContentReady && streams.length === 0) {
    return null;
  }

  return (
    <section
      ref={containerRef}
      className={sectionClass}
      aria-label={isLiveMode ? "other-live-sessions-heading" : "Previously live sessions"}
    >
      <div className={isLiveMode ? "pt-3 pb-1" : "mb-1"}>
        <h2
          {...(isLiveMode ? { id: "other-live-sessions-heading" } : {})}
          className={`font-medium text-[#1A1A1A] ${headingSizeClassName}`}
        >
          {title}
        </h2>
      </div>

      {!isContentReady && data && <HorizontalCardSkeleton count={skeletonCount} title={title} />}

      {isContentReady && (
        <LiveStreamsList
          streams={streams}
          isFetchingMore={isFetchingMore}
          isLiveMode={isLiveMode}
          onScrollEnd={handlePreviousScrollEnd}
          onThumbnailClick={handleThumbnailClick}
        />
      )}
    </section>
  );
}
